/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { AlertTriangle, ArrowRight, ChevronRight, ExternalLink, Plus, Power, PowerOff } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import type { IntegrationEnvironment } from '../../api/areas/integrations';
import type { ModuleInstance, ModuleRegistryConnection, ModuleSummary } from '../../api/areas/modules';
import { Badge } from '../../components/Badge';
import { DataTable, type Column } from '../../components/blocks';
import { ChangeDialog, StatusBadge, tx, When } from '../integrations/shared';
import { CreateInstanceForm, ToggleForm } from './forms';
import { DocList, HealthBadge, InstanceStatusBadge, modulesUrl, useCapabilityName, useInstanceLabel, useText } from './ui';

type Dialog = { kind: 'create' } | { kind: 'toggle'; on: boolean; instance: ModuleInstance } | null;

/**
 * One module of a branch (ADR-025 §8), compact: a header line (name, version, provider, what it
 * can do, connections used of the limit, "Thêm kết nối"), its summary and documents (folded), then
 * its connections (status, health, version in force, latest test), with "Bật", "Tắt" and "Mở".
 */
export function ModuleCard({
  summary,
  env,
  canManage,
  onChanged,
  connections,
  addConnectionTo,
}: {
  summary: ModuleSummary;
  env: IntegrationEnvironment;
  canManage: boolean;
  onChanged: () => void;
  /** Registry branch: the `registry.registries` connections this module carries (their own pages, ADR-016). */
  connections?: readonly ModuleRegistryConnection[];
  /** Registry branch: where a connection of this module is added (the connections page). */
  addConnectionTo?: string;
}) {
  const { t } = useTranslation('modules');
  const text = useText();
  const capability = useCapabilityName();
  const label = useInstanceLabel();
  const [dialog, setDialog] = useState<Dialog>(null);
  const m = summary.manifest;
  const max = m.instances.kind === 'one' ? 1 : m.instances.max;
  // The branch list carries the latest test only (`recent_tests` newest first); running one needs a version, on the connection's page.
  // Widths below are the card's (`@container`), not the screen's: from md the sidebar takes 256px of it.
  const testColumn: Column<ModuleInstance> = {
    key: 'test',
    header: t('instanceColumns.test'),
    className: 'hidden @xl:table-cell',
    cell: (i) => {
      const last = i.recent_tests[0];
      if (!last) return <span className="text-xs text-muted-foreground">{t('config.notTested')}</span>;
      return (
        <Link
          to={modulesUrl(m.branch, m.id, i.key, 'test')}
          title={t('test.title')}
          className="inline-flex flex-wrap items-center gap-1.5 rounded-sm text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          <Badge tone={last.outcome === 'OK' ? 'ok' : 'crit'}>{t(`test.outcome.${last.outcome}`)}</Badge>
          <When at={last.finished_at} />
        </Link>
      );
    },
  };
  const columns: Column<ModuleInstance>[] = [
    {
      key: 'name',
      header: t('instanceColumns.name'),
      // Wrap (cells default to nowrap) and tighten the gaps between columns so a phone keeps "Bật / Tắt / Mở" in view;
      // `pl-5` lines the table up with the card's (and every Panel's) `px-5`.
      className: 'whitespace-normal pl-5 @max-md:pr-1',
      cell: (i) => (
        <span className="grid gap-0.5">
          <Link to={modulesUrl(m.branch, m.id, i.key)} className="font-medium text-primary hover:underline">
            {label(i)}
          </Link>
          <span className="text-xs text-muted-foreground">
            <code>{i.key}</code> · {i.active ? t('card.versionInForce', { version: i.active.version }) : t('card.noVersion')}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('instanceColumns.status'),
      className: 'whitespace-normal @max-md:px-1',
      // `unknown` health of a connection switched off says nothing its status does not; switched on it means
      // no test and no call yet (healthHint.unknown), worth showing.
      cell: (i) => (
        <span className="flex flex-wrap gap-1">
          <InstanceStatusBadge status={i.status} />
          {i.health !== 'unknown' || i.enabled ? <HealthBadge health={i.health} /> : null}
        </span>
      ),
    },
    ...(m.test.kind === 'no_test' ? [] : [testColumn]),
    {
      key: 'actions',
      header: t('instanceColumns.actions'),
      className: 'pr-5 @max-md:pl-1 @max-md:pr-3',
      cell: (i) => (
        <span className="flex flex-wrap justify-end gap-1.5">
          {canManage && i.active && !i.pending ? (
            i.enabled ? (
              <Button size="xs" variant="outline" onClick={() => setDialog({ kind: 'toggle', on: false, instance: i })}>
                <PowerOff /> {t('card.switchOff')}
              </Button>
            ) : (
              <Button size="xs" variant="outline" onClick={() => setDialog({ kind: 'toggle', on: true, instance: i })}>
                <Power /> {t('card.switchOn')}
              </Button>
            )
          ) : null}
          <Button size="xs" variant="ghost" asChild>
            <Link to={modulesUrl(m.branch, m.id, i.key)}>
              {t('card.open')} <ArrowRight />
            </Link>
          </Button>
        </span>
      ),
    },
  ];
  const done = () => {
    onChanged();
  };
  const registryColumns: Column<ModuleRegistryConnection>[] = [
    {
      key: 'name',
      header: t('instanceColumns.name'),
      className: 'whitespace-normal pl-5 @max-md:pr-1',
      cell: (c) => (
        <span className="grid gap-0.5">
          <Link to={c.page} className="font-medium text-primary hover:underline">
            {c.name}
          </Link>
          <span className="text-xs text-muted-foreground">
            <code>{c.code}</code> · {tx(`kind.${c.connection_kind}`, c.connection_kind)} · {tx(`protocol.${c.protocol}`, c.protocol)}
          </span>
        </span>
      ),
    },
    { key: 'status', header: t('instanceColumns.status'), className: 'whitespace-normal @max-md:px-1', cell: (c) => <StatusBadge group="connectionStatus" value={c.status} /> },
    {
      key: 'actions',
      header: t('instanceColumns.actions'),
      className: 'pr-5 @max-md:pl-1 @max-md:pr-3',
      cell: (c) => (
        <span className="flex justify-end">
          <Button size="xs" variant="ghost" asChild>
            <Link to={c.page}>
              {t('card.open')} <ArrowRight />
            </Link>
          </Button>
        </span>
      ),
    },
  ];
  const used = summary.instances.length + (connections?.length ?? 0);
  return (
    <Card className="@container min-w-0 gap-0 py-0 shadow-xs" data-module={`${m.branch}/${m.id}`}>
      {/* Line 1: name, version, provider, what it can do; the count and "Thêm kết nối" on the right (wrapping under on phones). */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 px-5 pt-3">
        <h2 className="text-base leading-snug font-semibold">{text(m.name)}</h2>
        <span className="text-xs text-muted-foreground" title={t('card.version', { version: m.version })}>
          {t('card.versionShort', { version: m.version })}
        </span>
        {/* The dot wraps with the provider, never alone at the end of a line. */}
        <span className="inline-flex items-center gap-2 whitespace-nowrap">
          <span className="text-muted-foreground" aria-hidden="true">
            ·
          </span>
          <a href={m.vendor.website} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-0.5 rounded-sm text-sm text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50">
            <span className="sr-only">{t('card.vendor')}: </span>
            {m.vendor.name}
            <ExternalLink className="size-3" aria-hidden="true" />
          </a>
        </span>
        {m.capabilities.length ? (
          <ul className="flex flex-wrap gap-1" aria-label={t('card.capabilities')}>
            {m.capabilities.map((c) => (
              <li key={c}>
                <Badge tone="info" icon={false} title={c}>
                  {capability(c)}
                </Badge>
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-xs text-muted-foreground">
            {t('card.capabilities')}: {t('card.noCapabilities')}
          </span>
        )}
        <span className="ml-auto flex flex-wrap items-center gap-2">
          <span className="text-xs tabular text-muted-foreground">{t('card.limit', { used, count: max })}</span>
          {canManage && addConnectionTo ? (
            <Button size="sm" asChild>
              <Link to={addConnectionTo}>
                <Plus /> {t('card.add')}
              </Link>
            </Button>
          ) : canManage && summary.can_add ? (
            <Button size="sm" onClick={() => setDialog({ kind: 'create' })}>
              <Plus /> {t('card.add')}
            </Button>
          ) : null}
        </span>
      </div>
      {/* Line 2: what the module is for; under it the provider's documents, on a line of their own so the toggle stays put when opened. */}
      <div className="space-y-1 px-5 pt-1 pb-3 text-sm text-muted-foreground">
        <p className="min-w-0">{text(m.summary)}</p>
        {m.docs.length ? (
          <details className="group text-xs">
            <summary className="inline-flex cursor-pointer list-none items-center gap-1 rounded-sm font-medium hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 [&::-webkit-details-marker]:hidden">
              <ChevronRight className="size-3.5 transition-transform group-open:rotate-90" aria-hidden="true" />
              {t('card.documents')} ({m.docs.length})
            </summary>
            {/* DocList is text-sm; here it sits under a text-xs toggle. */}
            <div className="mt-1.5 pl-4.5 [&_ul]:text-xs">
              <DocList docs={m.docs} />
            </div>
          </details>
        ) : null}
      </div>
      {!m.production_ready ? (
        <p role="note" className="flex items-center gap-1.5 px-5 pb-3 text-xs">
          <AlertTriangle className="size-3.5 shrink-0 text-warning" aria-hidden="true" />
          {t('card.notProductionReady')}
        </p>
      ) : null}
      {connections?.length ? <DataTable className="border-t" columns={registryColumns} rows={connections} rowKey={(c) => c.id} /> : null}
      {summary.instances.length ? (
        <DataTable className="border-t" columns={columns} rows={summary.instances} rowKey={(i) => i.id} />
      ) : !used ? (
        <p className="border-t px-5 py-2.5 text-xs text-muted-foreground">{t('card.noInstances')}</p>
      ) : null}
      <ChangeDialog open={dialog?.kind === 'create'} onOpenChange={(o) => !o && setDialog(null)} title={t('create.title', { module: text(m.name) })} description={t('create.description')}>
        <CreateInstanceForm manifest={m} first={!summary.instances.length} env={env} onClose={() => setDialog(null)} onDone={done} />
      </ChangeDialog>
      <ChangeDialog
        open={dialog?.kind === 'toggle'}
        onOpenChange={(o) => !o && setDialog(null)}
        title={dialog?.kind === 'toggle' ? (dialog.on ? t('toggle.onTitle', { name: text(m.name) }) : t('toggle.offTitle', { name: text(m.name) })) : ''}
      >
        {dialog?.kind === 'toggle' ? <ToggleForm manifest={m} instance={dialog.instance} on={dialog.on} env={env} onClose={() => setDialog(null)} onDone={done} /> : null}
      </ChangeDialog>
    </Card>
  );
}
